import React from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

function codeOf(x) {
  return String(x?.code || '').toUpperCase();
}

function isUseSamePlan(a) {
  return codeOf(a) === 'USE_SAME_PLAN';
}

function isOpenPlanCreate(a) {
  return codeOf(a) === 'OPEN_PLAN_CREATE';
}

function isPackageUsageLimitWarn(w) {
  const code = codeOf(w);
  return (
    code.includes('USAGE_LIMIT') ||
    code.includes('RIGHT') ||
    code.includes('QUOTA') ||
    code.includes('SESSION_LIMIT') ||
    code.includes('PACKAGE_LIMIT')
  );
}

function isPackageDurationWarn(w) {
  const code = codeOf(w);
  return (
    code.includes('EXPIRE') ||
    code.includes('EXPIRED') ||
    code.includes('END_DATE') ||
    code.includes('PLAN_FINISH') ||
    code.includes('DURATION') ||
    code.includes('TIME')
  );
}

function SoftWarn({ title, text, meta }) {
  const theme = useMuiTheme();
  const main = theme.palette.warning.main;

  return (
    <MDBox
      sx={{
        mt: 0.8,
        p: 1,
        borderRadius: '14px',
        border: `1px solid ${alpha(main, 0.22)}`,
        bgcolor: alpha(main, 0.1),
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <Icon sx={{ fontSize: 18, color: main, mt: '2px' }}>warning_amber</Icon>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
          {title}
        </MDTypography>
        <MDTypography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 0.25 }}>
          {text}
        </MDTypography>
        {meta ? (
          <MDTypography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.25 }}>
            {meta}
          </MDTypography>
        ) : null}
      </MDBox>
    </MDBox>
  );
}

function InfoTip({ text }) {
  const theme = useMuiTheme();
  return (
    <Tooltip title={text} placement="top" arrow>
      <Icon
        sx={{
          fontSize: 18,
          opacity: 0.72,
          cursor: 'help',
          ml: 0.7,
          '&:hover': { opacity: 0.95 },
          color: theme.palette.text.primary,
        }}
      >
        info_outline
      </Icon>
    </Tooltip>
  );
}

export function AttendanceDecisionModal({ open, onClose, decision, onConfirm, onAction }) {
  const theme = useMuiTheme();
  if (!decision) return null;

  const globalBlocks = Array.isArray(decision?.blocks) ? decision.blocks : [];
  const participants = Array.isArray(decision?.participants) ? decision.participants : [];
  const hasBlocks =
    globalBlocks.length > 0 || participants.some((person) => (person?.blocks?.length || 0) > 0);

  const packageChoiceList = participants
    .map((person) => {
      const warnings = Array.isArray(person?.warnings) ? person.warnings : [];
      const actions = Array.isArray(person?.actions) ? person.actions : [];
      const createAction = actions.find(isOpenPlanCreate);
      const samePlanAction = actions.find(isUseSamePlan);
      const needsChoice = !!createAction && warnings.some(isPackageUsageLimitWarn);
      if (!needsChoice) return null;

      const mainWarn =
        warnings.find((warn) => codeOf(warn).includes('PACKAGE_WILL_FINISH'))?.message ||
        warnings.find(isPackageUsageLimitWarn)?.message ||
        'Paket hakki bu seansla bitiyor veya asiliyor.';

      const meta =
        person?.remaining && typeof person.remaining.normalRemaining === 'number'
          ? `Kalan hak: ${person.remaining.normalRemaining} -> Seans sonrasi: ${person.remaining.normalRemainingAfter}`
          : person?.plan?.planName
          ? `Paket: ${person.plan.planName}`
          : null;

      return {
        person,
        mainWarn,
        meta,
        warnings,
        createAction,
        samePlanAction,
      };
    })
    .filter(Boolean);

  const participantWarnList = participants
    .map((person) => {
      const warnings = Array.isArray(person?.warnings)
        ? person.warnings.filter(isPackageUsageLimitWarn)
        : [];
      if (!warnings.length) return null;

      const meta =
        person?.remaining && typeof person.remaining.normalRemaining === 'number'
          ? `Kalan hak: ${person.remaining.normalRemaining} -> Seans sonrasi: ${person.remaining.normalRemainingAfter}`
          : person?.plan?.planName
          ? `Paket: ${person.plan.planName}`
          : null;

      return {
        person,
        name: person?.memberFullName || `Katilimci #${person?.sessionParticipantId}`,
        meta,
        warnings,
      };
    })
    .filter(Boolean);

  const showOnlyChoices = packageChoiceList.length > 0;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '18px',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
          background: alpha(
            theme.palette.background.paper,
            theme.palette.mode === 'dark' ? 0.92 : 0.98
          ),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDBox
              sx={{
                width: 40,
                height: 40,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.info.main, 0.12),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Icon sx={{ fontSize: 20, color: theme.palette.info.main }}>info</Icon>
            </MDBox>

            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                {showOnlyChoices ? 'Paket karari gerekli' : hasBlocks ? 'Sorun var' : 'Bilgi'}
              </MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.8 }}>
                {showOnlyChoices
                  ? 'Paket hakki bitenler icin secim yap.'
                  : hasBlocks
                  ? 'Devam edemezsin.'
                  : 'Devam etmek istiyor musun?'}
              </MDTypography>
            </MDBox>
          </MDBox>

          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 1.2 }}>
        {globalBlocks.length > 0 ? (
          <MDBox sx={{ mb: 1 }}>
            {globalBlocks.map((block, index) => (
              <SoftWarn key={index} title="Engel" text={block?.message || String(block)} />
            ))}
          </MDBox>
        ) : null}

        {!showOnlyChoices && participantWarnList.length > 0 ? (
          <MDBox sx={{ display: 'grid', gap: 1 }}>
            {participantWarnList.map(({ person, name, meta, warnings }) => (
              <MDBox
                key={String(person?.sessionParticipantId)}
                sx={{
                  p: 1.2,
                  borderRadius: '18px',
                  border: `1px solid ${alpha(
                    theme.palette.divider,
                    theme.palette.mode === 'dark' ? 0.6 : 0.9
                  )}`,
                  bgcolor: alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === 'dark' ? 0.55 : 0.9
                  ),
                }}
              >
                <MDTypography variant="button" fontWeight="bold">
                  {name}
                </MDTypography>

                {warnings.map((warn, index) => (
                  <SoftWarn
                    key={index}
                    title="Uyari"
                    text={warn?.message || String(warn)}
                    meta={meta}
                  />
                ))}
              </MDBox>
            ))}
          </MDBox>
        ) : null}

        {showOnlyChoices ? (
          <MDBox sx={{ display: 'grid', gap: 1 }}>
            {packageChoiceList.map(
              ({ person, mainWarn, meta, warnings, samePlanAction, createAction }) => {
                const name = person?.memberFullName || `Katilimci #${person?.sessionParticipantId}`;

                return (
                  <MDBox
                    key={String(person?.sessionParticipantId)}
                    sx={{
                      p: 1.2,
                      borderRadius: '18px',
                      border: `1px solid ${alpha(
                        theme.palette.divider,
                        theme.palette.mode === 'dark' ? 0.6 : 0.9
                      )}`,
                      bgcolor: alpha(
                        theme.palette.background.paper,
                        theme.palette.mode === 'dark' ? 0.55 : 0.9
                      ),
                    }}
                  >
                    <MDTypography variant="button" fontWeight="bold">
                      {name}
                    </MDTypography>

                    <SoftWarn title="Paket hakki bitti" text={mainWarn} meta={meta} />
                    {warnings.filter(isPackageDurationWarn).map((warn, index) => (
                      <MDTypography
                        key={index}
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.6,
                          opacity: 0.65,
                          fontSize: 11,
                          lineHeight: 1.3,
                        }}
                      >
                        {warn?.message || String(warn)}
                      </MDTypography>
                    ))}
                    <MDBox
                      sx={{
                        mt: 1,
                        display: 'grid',
                        gridTemplateColumns: samePlanAction ? '1fr 1fr' : '1fr',
                        gap: 1,
                      }}
                    >
                      {samePlanAction ? (
                        <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
                          <MDButton
                            variant="outlined"
                            color="info"
                            onClick={() => onAction?.(samePlanAction, person)}
                            sx={{
                              borderRadius: '14px',
                              textTransform: 'none',
                              fontWeight: 900,
                              height: 44,
                              flex: 1,
                            }}
                          >
                            Mevcut paketle devam et
                          </MDButton>

                          <InfoTip text="Bu seans mevcut paketten duser. Sonradan paket duzeltmek gerekebilir." />
                        </MDBox>
                      ) : null}

                      <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
                        <MDButton
                          variant="gradient"
                          color="warning"
                          onClick={() => onAction?.(createAction, person)}
                          sx={{
                            borderRadius: '14px',
                            textTransform: 'none',
                            fontWeight: 900,
                            height: 44,
                            flex: 1,
                          }}
                        >
                          Yeni paket olustur
                        </MDButton>

                        <InfoTip text="Uyeye yeni paket tanimlarsin. Bu seans yeni pakete baglanir ve eski paket kapanabilir." />
                      </MDBox>
                    </MDBox>
                  </MDBox>
                );
              }
            )}
          </MDBox>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <MDButton
          variant="outlined"
          color="info"
          onClick={onClose}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, flex: 1, height: 44 }}
        >
          Kapat
        </MDButton>

        {!showOnlyChoices ? (
          <MDButton
            variant="gradient"
            color={hasBlocks ? 'warning' : 'success'}
            onClick={onConfirm}
            disabled={hasBlocks}
            sx={{
              borderRadius: '14px',
              textTransform: 'none',
              fontWeight: 900,
              flex: 1,
              height: 44,
            }}
          >
            Devam et
          </MDButton>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

AttendanceDecisionModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  decision: PropTypes.object,
  onConfirm: PropTypes.func,
  onAction: PropTypes.func,
};
